/**
 * Project - Main class that orchestrates Thumbnail, FullPage, Reduce, and Legend
 */

import type { ProjectData } from '@js/types'
import { COMPONENTS, CONSTANTS, CURSOR } from '@js/global'
import { setRandomPosition, setPageOverflowY, lookForClassInChildren, Marquee, Draggable, zIndexManager, SvgToggleButton, parseMarkdownLinks} from '@js/utils'
import { AssetManager } from '@js/loaders'
import css from '@css/sass/styles.module.scss'

/* ___________________________________________________________________________
|                       P R I V A T E   C L A S S E S                         |
|___________________________________________________________________________ */


// Project open
export class ImgGallery {
  private legend: Legend
  private video?: string | string[]
  private imgGallery?: string | string[]
  private elem: HTMLElement
  private imgs: (HTMLImageElement | HTMLIFrameElement)[] = []
  private currImg: number = 0

  private imgLeft:  string | undefined
  private imgRight: string | undefined

  // The clickable overlay zones on images
  private zoneLeft:  HTMLElement | null = null
  private zoneRight: HTMLElement | null = null

  // Buttons underneath videos
  private videoBtnContainer: HTMLElement | null = null
  protected videoBtnLeft:  HTMLImageElement | null = null
  protected videoBtnRight: HTMLImageElement | null = null

  constructor(elem: HTMLElement, imgGallery?: string | string[], video?: string | string[], legend?: Legend | null) {
    this.legend = legend!
    this.video = video
    this.imgGallery = imgGallery
    this.elem = elem

    const assets = AssetManager.getInstance()
    this.imgLeft  = assets.get('icons', 'left.svg')
    this.imgRight = assets.get('icons', 'right.svg')
  }

  setup(): ImgGallery {
    if (!this.imgGallery && !this.video) return this;

    // No need to search again — assume this.elem is already .img_gallery_container
    const container = this.elem;

    const alreadySetup = lookForClassInChildren(container, css.img_gallery);
    if (alreadySetup) return this;

    this.loadImages(container).then(() => {
      if (this.imgs.length > 0) this.initGallery();
    });

    return this;
  }

  /* -----------------------------------------------------------------------
  |  L O A D
  |----------------------------------------------------------------------- */

  async loadImages(container: HTMLElement): Promise<void> {
      // --- Images ---
      const names: string[] = Array.isArray(this.imgGallery)
          ? this.imgGallery
          : this.imgGallery ? [this.imgGallery] : []

      const assets = AssetManager.getInstance()
      const urls = await Promise.all(names.map(n => assets.getAsync('gallery', n)))
      const resolved = urls.filter((u): u is string => !!u)

      for (const url of resolved) {
          const img = document.createElement('img')
          img.className = css.img_gallery
          img.src = url
          img.style.display = 'none'
          container.appendChild(img)
          this.imgs.push(img)
      }

      // --- Video ---
      if (this.video) {
          const videoSrcs = Array.isArray(this.video) ? this.video : [this.video]
          for (const src of videoSrcs) {
              const iframe = this.createIframe(container, src)
              this.imgs.push(iframe)
          }
      }

      // ── Only add navigation if there's more than one item ──────────────
      const total = this.imgs.length
      if (total > 1) {
          if (resolved.length > 0) {
              this.zoneLeft  = this.createClickZone(container, 'left',  () => this.nextImg(-1))
              this.zoneRight = this.createClickZone(container, 'right', () => this.nextImg(1))
          }
          if (this.video) {
              this.createVideoBtns(container)
          }
      }
  }

  /* -----------------------------------------------------------------------
  |  C R E A T E   E L E M E N T S
  |----------------------------------------------------------------------- */

  private createClickZone(parent: HTMLElement, side: 'left' | 'right', onClick: () => void): HTMLElement {
    const zone = document.createElement('div')
    zone.className = side === 'left' ? css.zone_left : css.zone_right
    zone.style.cssText = 'position:absolute; top:0; height:100%; width:50%; cursor:none; z-index:2;'
    zone.style[side] = '0'
    parent.appendChild(zone)

    zone.addEventListener('click', onClick)
    zone.addEventListener('mouseenter', () => CURSOR.cursor?.setDisplay(true, side))
    zone.addEventListener('mouseleave', () => CURSOR.cursor?.setDisplay(false))

    return zone
  }

  private createIframe(parent: HTMLElement, src: string): HTMLIFrameElement {
    const outer = document.createElement('div')
    outer.className = css.video_iframe_outer
    parent.appendChild(outer)

    const iframe = document.createElement('iframe')
    iframe.src = src
    iframe.setAttribute('webkitallowfullscreen', '')
    iframe.setAttribute('mozallowfullscreen', '')
    iframe.setAttribute('allowfullscreen', '')
    iframe.style.display = 'none'
    outer.appendChild(iframe)

    this.makeResponsive(iframe)
    return iframe
  }

  private makeResponsive(iframe: HTMLIFrameElement): void {
    const outer = iframe.parentElement as HTMLElement
    if (!outer) return

    const aspectRatio = iframe.clientHeight / iframe.clientWidth || 9 / 16

    iframe.removeAttribute('height')
    iframe.removeAttribute('width')

    const resize = () => {
      iframe.style.width  = `${outer.clientWidth}px`
      iframe.style.height = `${outer.clientWidth * aspectRatio}px`
    }
    window.addEventListener('resize', resize)
    resize()
  }

  private createVideoBtns(parent: HTMLElement): void {
    if (!this.imgLeft || !this.imgRight) return

    this.videoBtnContainer = document.createElement('div')
    this.videoBtnContainer.className = css.video_btn_container
    this.videoBtnContainer.style.cssText = 'display:none; flex-direction:row; justify-content:center; gap:16px; margin-top:8px;'
    parent.appendChild(this.videoBtnContainer)

    this.videoBtnLeft  = this.createVideoBtn(this.videoBtnContainer, this.imgLeft,  () => this.nextImg(-1))
    this.videoBtnRight = this.createVideoBtn(this.videoBtnContainer, this.imgRight, () => this.nextImg(1))
  }

  private createVideoBtn(parent: HTMLElement, src: string, onClick: () => void): HTMLImageElement {
    const btn = document.createElement('img')
    btn.src = src
    btn.style.cssText = 'width:32px; height:32px; cursor:pointer;'
    btn.addEventListener('click', onClick)
    parent.appendChild(btn)
    return btn
  }

  /* -----------------------------------------------------------------------
  |  G A L L E R Y   L O G I C
  |----------------------------------------------------------------------- */

  initGallery(): void {
    this.showImg(0)
  }

  nextImg(incr: number): void {
    this.hideImg(this.currImg)
    this.currImg = (this.currImg + incr + this.imgs.length) % this.imgs.length
    this.showImg(this.currImg)
    this.legend?.update(this.currImg)
  }

  private hideImg(i: number): void {
    const img = this.imgs[i]
    if (!img) return
    const el = img.nodeName === 'IFRAME' ? img.parentElement! : img
    el.style.display = 'none'
    this.updateChrome(false)
  }

  private showImg(i: number): void {
    const img = this.imgs[i]
    if (!img) return

    const isVideo = img.nodeName === 'IFRAME'
    const el = isVideo ? img.parentElement! : img
    el.style.display = isVideo ? 'block' : 'block'
    img.style.display = 'block'

    this.updateChrome(!isVideo)
    if (isVideo && this.videoBtnContainer) {
      this.videoBtnContainer.style.display = 'flex'
    }
  }

  private updateChrome(showZones: boolean): void {
    const display = showZones ? 'block' : 'none'
    if (this.zoneLeft)  this.zoneLeft.style.display  = display
    if (this.zoneRight) this.zoneRight.style.display = display
    if (!showZones && this.videoBtnContainer) {
      this.videoBtnContainer.style.display = 'none'
    }
  }

  /* -----------------------------------------------------------------------
  |  U T I L S
  |----------------------------------------------------------------------- */

  updateLegend(i: number): void { this.legend?.update(i) }

  resetImgCount(): void { this.currImg = 0 }
}

export class Legend {
  private parent: HTMLElement;
  private data: ProjectData;
  private el: HTMLElement | null = null;

  // Cache for image → base ID mapping (computed once)
  private imageIdMap: Map<number, number> = new Map(); // image index → base id

  constructor(parent: HTMLElement, data: ProjectData) {
    this.parent = parent;
    this.data = data;
    this.computeImageIdMap();
  }

  /**
   * Pre-compute base ID for each gallery image
   */
  private computeImageIdMap(): void {
    const gallery = this.data._imgGallery ?? [];
    gallery.forEach((path, index) => {
      const filename = path.split(/[\\/]/).pop() ?? '';
      const match = filename.match(/^(\d+)/); // starts with one or more digits
      if (match) {
        const baseId = parseInt(match[1], 10);
        this.imageIdMap.set(index, baseId);
      }
      // else: no numeric prefix → no ID → won't match any cartel.id
    });

    // Optional: warn if there are images without ID but some cartels have id
    const cartelsWithId = (Array.isArray(this.data.cartels) ? this.data.cartels : [this.data.cartels ?? {}])
      .filter(c => c?.id !== undefined).length;

    if (cartelsWithId > 0 && this.imageIdMap.size < gallery.length) {
      console.warn(
        `[Legend] Project "${this.data.title}" has ${gallery.length} images ` +
        `but only ${this.imageIdMap.size} have numeric prefix. ` +
        `Some images will show default/no cartel.`
      );
    }
  }

  private formatCoauthor(coauthor: string | string[] | undefined): string {
    if (!coauthor) return ''

    if (typeof coauthor === 'string') {
      return `avec ${coauthor}`
    }

    if (coauthor.length === 0) return ''
    if (coauthor.length === 1) return `avec ${coauthor[0]}`
    if (coauthor.length === 2) return `avec ${coauthor[0]} et ${coauthor[1]}`

    const allButLast = coauthor.slice(0, -1).join(', ')
    const last = coauthor[coauthor.length - 1]
    return `avec ${allButLast} et ${last}`
  }

  setupLists(): void {
    this.el = document.createElement('h4');
    this.el.className = css.legende;
    this.parent.appendChild(this.el);

    this.update(0);
  }

  update(imageIndex: number = 0): void {
    if (!this.el) return

    const getValue = (v: string | string[] | undefined): string => {
      if (!v) return ''
      return Array.isArray(v) ? v.join(' — ') : v
    }

    const currentBaseId = this.imageIdMap.get(imageIndex) ?? null

    const cartels = Array.isArray(this.data.cartels)
      ? this.data.cartels
      : this.data.cartels ? [this.data.cartels] : []

    const matchingCartels = cartels.filter(c => {
      if (c.id === undefined) return true
      return c.id === currentBaseId
    })

    // Capitalize helper
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

    // Type as header (before cartel loop)
    const typeHeader = this.data.type ? `<span>${getValue(this.data.type)}</span><br><br>` : ''

    const cartelStrings = matchingCartels.map(cartel => {
      // isVideo — show a simple indicator, skip other fields
      if (cartel.isVideo) {
        const parts = ['vidéo']
        if (cartel.duration) parts.push(getValue(cartel.duration))
        return parts.join(', ')
      }

      const cartelParts: string[] = []

      // Subtitle fallback to title
      const subtitle = cartel.subtitle ?? this.data.title
      if (subtitle) cartelParts.push(`<i>${capitalize(getValue(subtitle))}</i>`)

      if (cartel.coauthor)   cartelParts.push(this.formatCoauthor(cartel.coauthor))
      if (this.data.type)    cartelParts.push(getValue(this.data.type))
      if (cartel.technique)  cartelParts.push(getValue(cartel.technique))
      if (cartel.dimensions) cartelParts.push(getValue(cartel.dimensions))
      if (cartel.pages)      cartelParts.push(getValue(cartel.pages))
      if (cartel.duration)   cartelParts.push(getValue(cartel.duration))
      if (this.data.date)    cartelParts.push(getValue(this.data.date))

      return cartelParts.join(', ')
    })
    this.el.innerHTML = typeHeader + cartelStrings.join('<br><br>')

  }
}

export class FullPage {

  // Data
  private data: ProjectData
  private title: string
  private name: string
  private thumbImgContainer: HTMLElement
  
  // Main elements
  private element: HTMLElement | null = null
  private btnCloseElement: HTMLImageElement | null = null
  private left: HTMLElement | null = null
  private middle: HTMLElement | null = null
  private right: HTMLElement | null = null
  private imgGallery: HTMLElement | null = null
  
  // images
  private imgClose1: string | undefined
  private imgClose2: string | undefined

  // Components
  private project: Project
  private legend: Legend | null = null

  constructor(data: ProjectData, project: Project, thumbImgContainer: HTMLElement) {
    this.data = data
    this.name = data.name ?? ''
    this.title = data.title ?? ''
    this.project = project
    this.thumbImgContainer = thumbImgContainer

    const assets = AssetManager.getInstance()
    this.imgClose1 = assets.get('icons', 'croix.svg')
    this.imgClose2 = assets.get('icons', 'croix_2.svg')
  }

  /**
   * Create the full page view
   */
  setup(closeCallback: () => void): void {

    if (!this.title) {
      console.warn('[FullPage] creation aborted: no title for', this.name)
      return
    }
    
    this.element = this.createElement()
    this.setupBtnClose(this.element, "btn_close_fullpage")
    this.createLayout()
    this.btnCloseElement?.addEventListener('click', () => closeCallback());

    console.debug(`[FullPage] created: ${this.name}`)
  }


  /* Getters */
  getElement(): HTMLElement | null {
    return this.element
  }

  getCloseButton(): HTMLImageElement | null {
    return this.btnCloseElement
  }

  getLegend(): Legend | null {
    return this.legend
  }

  getLeftSection(): HTMLElement | null {
    return this.left
  }

  getMiddleSection(): HTMLElement | null {
    return this.middle
  }

  getRightSection(): HTMLElement | null {
    return this.right
  }

  getImageGallery(): HTMLElement | null {
    return this.imgGallery
  }

  /* Private methods */
  private createElement(): HTMLElement {
    const element = document.createElement('div')
    element.id = `fp_${this.name}` // not currently using it
    element.className = css.fullpage
    element.style.display = 'none' // Hidden by default
    document.body.appendChild(element)
    return element
  }

  private setupBtnClose(parent: HTMLElement, id: string): SvgToggleButton | null {
    /**
     * Setup the close button (non-toggle, uses close icons only)
     * Clicking it is handled externally by Reduce.
     */
    if (!this.imgClose1 || !this.imgClose2 ) {
      console.warn("Buttons are missing. not initializing")
      return null
    }

    const btn = new SvgToggleButton({
      parent,
      id,
      images: {default: this.imgClose1, hover: this.imgClose2, active: this.imgClose2 },
      imagesToggled: {default: this.imgClose1, hover: this.imgClose2, active: this.imgClose2 },
      floatStyle: 'right',
      className: css.btnClose,
    })
    this.btnCloseElement = btn.element; // store the <img>


  // ─── Hover effect on close button → fade full page ────────────────
  if (this.element) {
    const fullPageBox = this.element; // the full page container

    btn.element.addEventListener('mouseenter', () => {
      fullPageBox.style.opacity = '0.5';
    });

    btn.element.addEventListener('mouseleave', () => {
      fullPageBox.style.opacity = '1';
    });
  }

    console.debug("[Project] '" + this.name + "' button close set up.")
    return btn
  }

  private createLayout(): void {
    if (!this.element) return
    
    const mainContainer = document.createElement('div')
    mainContainer.classList.add(css.middle_and_right_container)
    this.element.appendChild(mainContainer)
    
    // 1. left container
    this.left = document.createElement('div')
    this.left.className = css.left
    this.element.appendChild(this.left)

    const imgClone = this.thumbImgContainer.cloneNode(true) as HTMLElement
    imgClone.style.visibility = 'visible'
    this.left.appendChild(imgClone)
    
    this.legend = new Legend(this.left, this.data)
    this.legend.setupLists()

    // 2. middle containers
    const midContainer = this.element?.querySelector(`.${css.middle_and_right_container}`)
    if (!midContainer) return
    
    this.middle = document.createElement('div')
    this.middle.classList.add(css.middle, css.unselectable_text)
    midContainer.appendChild(this.middle)
    
    this.imgGallery = document.createElement('div')
    this.imgGallery.classList.add(css.img_gallery_container, css.unselectable_text)
    this.middle.appendChild(this.imgGallery)
    
    // 3. right container
    this.right = document.createElement('div')
    this.right.className = css.right
    midContainer.appendChild(this.right)

    const title = document.createElement('h1')
    title.innerHTML = this.title
    this.right.appendChild(title)
    
    this.createTextContent(this.data.infos)
  }

  private createTextContent(texts: string[] | undefined): void {
    if (!this.right || !texts) return
    
    const fontSize = this.determineTextSize(texts)
    
    texts.forEach(text => {
      if (!text) return
      
      if (Array.isArray(text)) text = text.join('\n')
      text = text.replace(/"+/g, '')

      const p = document.createElement('p')
      p.classList.add(fontSize)
      p.innerHTML = parseMarkdownLinks(text) // ← links rendered inline
      this.right!.appendChild(p)

      // Prevent parent handlers from swallowing link clicks
      p.querySelectorAll('a').forEach(a => {
        a.addEventListener('click', e => e.stopPropagation())
      })

      this.right!.appendChild(document.createElement('br'))
    })
    
    const spacer = document.createElement('div')
    spacer.classList.add(css.spacer)
    this.right.appendChild(spacer)
  }

  private determineTextSize(texts: (string | undefined)[]): string {
    const totalLength = texts
      .filter(Boolean)
      .reduce((sum, text) => sum + (text?.length ?? 0), 0)
    
    if (totalLength < 150) return 'p3'
    if (totalLength < 550) return 'p2'
    return 'p1'
  }

  /**
   * Setup linked projects (must be called after Reduce is created)
   */
  setupLinkedProjects(): void {
    if (!this.data._computedLinkedNames?.length || !this.left) {
      return;
    }

    // Optional: sort alphabetically / by date / etc.
    const sortedNames = [...this.data._computedLinkedNames].sort();

    sortedNames.forEach(projectName => {
      this.createLinkedProject(this.project, projectName);
    });
  }

  private createLinkedProject(currentProject: Project, projectName: string): void {
    if (!this.left) return;

    // Try to find project by name (more reliable than title)
    const targetProject = COMPONENTS.projects.objects.find(
      p => p.getData().name === projectName
    );

    if (!targetProject) {
      return;
    }
    const displayText = targetProject.getData().title.toUpperCase();

    const link = document.createElement('p');
    link.classList.add(css.links);
    link.textContent = displayText;
    link.style.cursor = 'pointer';
    this.left.appendChild(link);

    link.addEventListener('click', (e) => {
      e.stopPropagation()
      currentProject.close()           // close current
      targetProject.open()                // open target
      console.log(`[Project] Linked project opened: ${displayText} (${projectName})`)
    });
  }
}

// Project closed
export class Thumbnail {
  // Core properties
  private data: ProjectData
  index: number
  private id: string
  
  // DOM elements
  private element: HTMLElement | null = null
  private titleElement: HTMLElement | null = null
  private typeDateElement: HTMLElement | null = null
  private imageContainer: HTMLElement | null = null
  private image: HTMLImageElement | null = null
  
  // Components
  draggable: Draggable | null = null
  titleMarquee: Marquee | null = null
  typeDateMarquee: Marquee | null = null

  constructor(data: ProjectData, index: number) {
    this.data = data
    this.index = index
    this.id = `thumbnail${index}`

  }

  /**
   * Setup the thumbnail
   */
  setup(onClick?: () => void): void {
    this.createThumbnailElement()
    this.setupDragging(onClick)
    this.setupHover()
    this.createMarquees()
    this.createImage()
    console.debug('[Project] thumbnail data:', this.data)
    
    this.positionRandomly()
  }

  /**
   * Get the thumbnail element
   */
  getElement(): HTMLElement | null {
    return this.element
  }

  /**
   * Get the image container
   */
  getImageContainer(): HTMLElement | null {
    return this.imageContainer
  }

  /* Private methods */

  private createThumbnailElement(): void {
    this.element = document.createElement('div')
    this.element.id = this.id
    this.element.className = `${css.thumbnail} ${css.unselectable_text}`
    this.element.style.width = `${CONSTANTS.css.boxWidth}px`
    this.element.style.height = `${CONSTANTS.css.boxWidth}px`
    this.element.style.position = 'absolute'
    this.element.style.visibility = 'visible'
    
    document.body.appendChild(this.element)
    
    zIndexManager.register(this.id, this.element)
  }

  private setupDragging(onClick?: () => void): void {
    if (!this.element) return
    
    this.draggable = new Draggable(this.element, {
      containment: 'window',
      clickThreshold: 5,
      onClick: () => {
        if (onClick) {
          console.debug(`[Thumbnail] ${this.id} clicked`)
          onClick()
        }
      },
      onStart: () => {
        zIndexManager.bringToFront(this.id)
      },
      onDrag: () => {
        zIndexManager.bringToFront(this.id)
      }
    })
  }

  private setupHover(): void {
    if (!this.element) return
    
    this.element.addEventListener('mouseenter', () => {
      zIndexManager.bringToFront(this.id)
      this.showMarquees()
      this.showHoverImage()
    })
    
    this.element.addEventListener('mouseleave', () => {
      this.hideMarquees()
      this.showDefaultImage()
    })
  }

  private createMarquees(): void {
    if (!this.element || !this.data.title) return
    
    // Title marquee (left direction - default)
    this.titleElement = this.createMarqueeElement(css.title, {
      text: this.data.title.toUpperCase(),
      direction: 'left', 
      speed: 40
    })
    
    // Type and date marquee (right direction)
    const typeDate = [this.data.type, this.data.date]
      .filter(Boolean)
      .join(' — ')
    
    if (typeDate) {
      this.typeDateElement = this.createMarqueeElement(css.type, {
        text: typeDate,
        direction: 'right',
        speed: 25
      })
    }
  }

  private createMarqueeElement(
    containerClass: string,
    options: { text: string; direction?: 'left' | 'right'; speed?: number }
  ): HTMLElement {
    if (!this.element) throw new Error('Thumbnail element not created')
    
    // Create container
    const container = document.createElement('div')
    container.classList.add(containerClass)
    this.element.appendChild(container)
    
    // Create marquee element
    const el = document.createElement('h3')
    el.classList.add(css.marquee, css.unselectable_text)
    container.appendChild(el)
    
    // Handle @ separator
    const texts = options.text.includes('@') 
      ? options.text.split('@')[0] 
      : options.text
    
    // ✅ FIX: Start hidden, will show on hover
    el.style.visibility = 'hidden'
    
    // Create and start marquee with direction
    const marquee = new Marquee(el, {
      texts,
      direction: options.direction || 'left',
      speed: options.speed
    })
    marquee.start()
    
    // Store marquee reference
    if (containerClass === css.title) {
      this.titleMarquee = marquee
    } else if (containerClass === css.type) {
      this.typeDateMarquee = marquee
    }
    
    return el
  }

  // Show marquee text on hover
  private showMarquees(): void {
    if (this.titleElement) {
      this.titleElement.style.visibility = 'visible'
    }
    if (this.typeDateElement) {
      this.typeDateElement.style.visibility = 'visible'
    }
  }

  // Hide marquee text when not hovering
  private hideMarquees(): void {
    if (this.titleElement) {
      this.titleElement.style.visibility = 'hidden'
    }
    if (this.typeDateElement) {
      this.typeDateElement.style.visibility = 'hidden'
    }
  }

  private createImage(): void {
    if (!this.element || !this.data.thumbnail.imgNormal) return
    
    // Create body container
    const body = document.createElement('div')
    body.className = css.body
    this.element.appendChild(body)
    
    // Create image container
    this.imageContainer = document.createElement('div')
    this.imageContainer.className = `${css.img_thumbnail} ${css.unselectable_text}`
    body.appendChild(this.imageContainer)
    
    // Create image
    this.image = document.createElement('img')
    if (this.data.thumbnail.imgNormal) {
      this.image.src = this.data.thumbnail.imgNormal
    }

    this.imageContainer.appendChild(this.image)
    
    // Setup dual image hover if second image exists
    if (this.data.thumbnail.imgHovered) {
      this.image.dataset.img1 = this.data.thumbnail.imgNormal
      this.image.dataset.img2 = this.data.thumbnail.imgHovered
    } 
    
    // Apply size
    this.applySize()
  }

  /**
   * Show hover image (image 1)
   */
  private showHoverImage(): void {           // mouseenter
    if (!this.image) return;
    if (this.data.thumbnail.imgHovered) {
      this.image.src = this.data.thumbnail.imgHovered;   // ← was wrong
    }
  }

  /**
   * Show default image (image 2)
   */
  private showDefaultImage(): void {         // mouseleave
    if (!this.image) return;
    this.image.src = this.data.thumbnail.imgNormal || this.image.src;
  }
  private applySize(): void {
    if (!this.data.thumbnail.size || !this.image || !this.element) return
    
    const [width, height] = this.data.thumbnail.size
    this.image.style.width = `${width}px`
    this.element.style.height = `${height}px`
  }
  private positionRandomly(): void {
    if (!this.element) return
    
    setRandomPosition(this.element)
    
    // Reposition on window resize
    window.addEventListener('resize', () => {
      if (this.element) {
        setRandomPosition(this.element)
      }
    })
  }
}


/* ___________________________________________________________________________
|                             M A I N   C L A S S                             |
|___________________________________________________________________________ */

export class Project {
  // var
  private isOpen: boolean = false;

  // Data
  private data: ProjectData
  private name: string
  private index: number
  
  // Components
  private thumbnail: Thumbnail | null = null
  private fullPage: FullPage | null = null
  
  constructor(data: ProjectData, index: number) {
    this.data = data
    this.name = data.name
    this.index = index
  }

  /**
   * Initialize and display the project (PHASE 1)
   */
  setup(): void {
    // 1. Create thumbnail and pass onClick callback that opens the project
    this.thumbnail = new Thumbnail(this.data, this.index)
    this.thumbnail.setup(() => this.open())

    // 2. Create full page
    const imgContainer = this.thumbnail.getImageContainer()
    if (!imgContainer) {
      console.warn(`[Project] No image container for project ${this.name}`)
      return
    }
    this.fullPage = new FullPage(this.data, this, imgContainer)
    this.fullPage?.setup(() => this.close())
    
    console.debug(`[Project] setup complete: '${this.name}'`)
  }

  /**
   * Create links between projects (PHASE 2)
   */
  createLinks(): void {
    if (this.fullPage) {
      this.fullPage.setupLinkedProjects()
    }
  }

  /**
   * Get the thumbnail element
   */
  getThumbnailElement(): HTMLElement | null {
    return this.thumbnail?.getElement() ?? null
  }

  /**
   * Get the full page element
   */
  getFullPageElement(): HTMLElement | null {
    return this.fullPage?.getElement() ?? null
  }

  getData(): ProjectData {
    return this.data
  }

  /**
   * Open/close the full page view
   */
  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    const fullEl = this.fullPage?.getElement();
    if (fullEl) fullEl.style.display = 'flex';
    setPageOverflowY(false);

    const middle = this.fullPage?.getMiddleSection();
    if (!middle) {
      console.warn('[Project] Middle section not found for', this.data.title);
      return;
    }

    // ────────────────────────────────────────────────
    // Pass .middle — NOT the inner container
    // ────────────────────────────────────────────────
    const galleryData = this.data._imgGallery;   // ← also fix property name (you had _imgGallery)
    const videoData   = this.data.video;
    const legend      = this.fullPage?.getLegend();

    if (!galleryData && !videoData) {
      console.debug('[Project] No gallery/video for', this.data.title);
      return;
    }

    // Create once (you can store it if you want to reuse later)
    const imgGallery = new ImgGallery(middle, galleryData, videoData, legend);
    imgGallery.setup();

    console.log('[Project] ImgGallery created & setup for', this.data.title);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.fullPage?.getElement()) {
      this.fullPage.getElement()!.style.display = 'none';
    }
    setPageOverflowY(true);

    // Optional: reset scroll / gallery position
  }

}
